import React, {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';

import { useDebounced } from '../hooks/use-debounced';
import SuggestBlock from './suggest-block';
import { getConfig, getTranslation } from '../utils/config';
import { getSuggest } from '../api/suggest';
import { useDfContext } from '../context/df-context';
import { SET_TERM } from '../context/actions';
import { PAGE_VARIANT } from '../constants';
import { ErrorContext } from '../api/error-boundary';

let timeout: number | undefined = undefined;

export default function SearchForm({
  placeholder = getTranslation('search_placeholder'),
  variant,
}: {
  placeholder?: string;
  variant: string;
}): React.ReactElement {
  const {
    dispatch,
    state: { term = '' },
  } = useDfContext();
  const form = useRef() as React.MutableRefObject<HTMLFormElement>;
  const inputField = useRef<HTMLInputElement>() as React.MutableRefObject<HTMLInputElement>;
  const [value, setValue] = useState<string>(term.trim().replace(/\s+/, ' '));
  const [autosuggestVisible, setAutosuggestVisible] = useState<boolean>(false);
  const [termSelected, setTermSelected] = useState<boolean>(true);
  const [suggestItems, setSuggestItems] = useState<string[]>([]);
  const [suggestDownloads, setSuggestDownloads] = useState<string[]>();
  const [downloadAmount, setDownloadAmount] = useState<number>(0);
  const errorContext = useContext(ErrorContext);

  useEffect(() => {
    if (term === '') {
      setValue('');
    }
  }, [term]);

  const resetSuggestions = () => {
    setSuggestItems([]);
    setSuggestDownloads([]);
    setDownloadAmount(0);
  };

  const handleTypeahead = useCallback(
    async (overrideValue) => {
      const usedValue = overrideValue || value;

      if (usedValue.length < 3 || termSelected) {
        resetSuggestions();
        setAutosuggestVisible(false);
        return;
      }

      let result;
      try {
        result = await getSuggest(
          usedValue,
          getConfig('cms_native_language', 'EN-EN'),
          getConfig('pim_language', 'en'),
        );
      } catch (e) {
        console.log(e);
        errorContext?.setError((e as any).message);
      }

      if (result?.data && result?.error === 0) {
        const { typeahead, suggestions } = result.data;
        setSuggestItems(typeahead);
        setSuggestDownloads(
          // Filter unique values for download suggestions
          suggestions.entries.filter((value, index, array) => array.indexOf(value) === index),
        );
        setDownloadAmount(suggestions.numFound);

        if ((typeahead.length > 0 || suggestions.numFound > 0) && !overrideValue) {
          clearTimeout(timeout);
          setAutosuggestVisible(true);
        } else {
          setAutosuggestVisible(false);
        }
      }
    },
    [value, termSelected, errorContext],
  );

  useDebounced(handleTypeahead, [value], 400);

  const handleTerm =
    (term: string, changeValue = true) =>
    (e?: MouseEvent<HTMLButtonElement>) => {
      if (changeValue) setValue(term);
      if (variant !== PAGE_VARIANT.RESULT) {
        setTimeout(() => {
          form.current?.submit();
        }, 0);
      } else {
        dispatch({
          type: SET_TERM,
          term,
        });
        if (e) {
          (e.target as HTMLElement).blur();
        }
        setTermSelected(true);
        setAutosuggestVisible(false);
        inputField.current?.blur();
      }
    };

  const handleSubmit = (e: MouseEvent<HTMLFormElement>) => {
    const trimmedTerm = value.trim();
    const hasTerm = trimmedTerm.length > 0;

    if (!hasTerm) {
      setValue(trimmedTerm);
    }

    if (!hasTerm || variant === PAGE_VARIANT.RESULT) {
      e.preventDefault();
    }

    if (hasTerm) {
      handleTerm(value, false)();
    }
  };

  return (
    <form
      action={getConfig('resultsPage')}
      method="GET"
      className="df-search-modul__form"
      ref={form}
      onSubmit={handleSubmit}
    >
      <div className="df-autosuggest-wrapper">
        <label className="df-search-modul__label">
          <span className="hidden">{getTranslation('search_subtitle')}</span>
          <input
            type="search"
            placeholder={placeholder}
            id="search-dlf"
            name="search"
            autoComplete="off"
            className="df-search-modul__search"
            value={value}
            onChange={(e) => {
              setValue(e.target.value.replace(/\s+/g, ' '));
            }}
            onBlur={() => {
              timeout = setTimeout(() => {
                setAutosuggestVisible(false);
              }, 400) as unknown as number;
            }}
            onFocus={() => {
              if ((suggestItems.length > 0 || downloadAmount > 0) && !termSelected) {
                clearTimeout(timeout);
                setAutosuggestVisible(true);
              } else {
                setTermSelected(false);
              }
            }}
            ref={inputField}
          />
        </label>
        <button aria-label={getTranslation('search')} className="df-search-modul__button"></button>
        {autosuggestVisible && (
          <div className="df-autosuggest">
            {suggestItems.length > 0 && (
              <SuggestBlock
                items={suggestItems}
                label={getTranslation('searchsuggestions')}
                searchTerm={value}
                handleSelection={handleTerm}
                highlightTerm
              />
            )}
            {downloadAmount > 0 && (
              <SuggestBlock
                items={suggestDownloads || []}
                label={getTranslation('downloadsuggestions')}
                searchTerm={value}
                handleSelection={handleTerm}
                quoteSelection={true}
              />
            )}
            {downloadAmount > 0 && (
              <button className="df-btn df-btn--simple df-autosuggest__show-all-results">
                {downloadAmount === 1 ? (
                  <span>{getTranslation('result_counter', downloadAmount)}</span>
                ) : (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: getTranslation('show_all_counted_downloads', downloadAmount),
                    }}
                  />
                )}
                <span className="df-btn__icon-right df-icon-arrow_long_right_red"></span>
              </button>
            )}
          </div>
        )}
      </div>
    </form>
  );
}
